import { useQuery } from '@tanstack/react-query';
import { Group, SiteMini, page } from '@cloudsign/shared';
import { z } from 'zod';
import { api } from '../../lib/api';

export const SAFETY = '/safety-check';
export const COMPLIANCE = '/compliance';
export const NFC = '/nfc-cards';

const GroupList = z.array(Group);
/** GET /sites answers with id and name only to people without sites.view. */
const SitePage = page(SiteMini);

export function useSiteOptions() {
  return useQuery({
    queryKey: ['w18', 'sites'],
    queryFn: () =>
      api.get('/sites', {
        query: { status: 'active', pageSize: 200 },
        schema: SitePage,
      }),
    select: (p) => p.items,
  });
}

export function useGroupOptions() {
  return useQuery({
    queryKey: ['w18', 'groups'],
    queryFn: () => api.get('/groups', { query: { status: 'active' }, schema: GroupList }),
  });
}

/** A date such as 2026-11-03 shown as 3 Nov 2026 (no time zone shifts: it is a calendar date). */
export function formatDay(day: string | null | undefined): string {
  if (!day) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!m) return day;
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** Today as a calendar date in the browser's zone. */
export function todayDay(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Whole days from today to a calendar date (negative = in the past). */
export function daysUntil(day: string): number {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  const a = Date.UTC(y, m - 1, d);
  const [ty, tm, td] = todayDay().split('-').map(Number) as [number, number, number];
  return Math.round((a - Date.UTC(ty, tm - 1, td)) / 86_400_000);
}
